export const CONVERSATION_CONTROL_INSTRUCTION_MAX_LENGTH = 15000;

export const DEFAULT_WAIT_FOR_CUSTOMER_INSTRUCTION = 'Use when the customer temporarily asks the assistant to pause, hold, stop and wait, or give them a moment. Use only when they intend to continue; this does not end the call.\nUse only when the customer explicitly asks to wait, pause or hold for sometime.\nDefault wait time - 20 seconds';

export const DEFAULT_LONG_USER_INPUT_INSTRUCTION = 'Use before asking a question whose answer may take time or contain natural pauses, such as a CRSF code, mobile number, date of birth, address, verification code, identifier, account information, or multi-part response. Use normal conversation flow for ordinary short answers.';

export const CONVERSATION_CONTROL_DEFAULTS = {
  user_wait_stop_signal: {
    enabled: 'No',
    max_wait_seconds: 20,
    instruction: DEFAULT_WAIT_FOR_CUSTOMER_INSTRUCTION,
  },
  long_user_input_handling: {
    enabled: 'No',
    continuation_timeout_seconds: 2.0,
    instruction: DEFAULT_LONG_USER_INPUT_INSTRUCTION,
  },
  accept_external_context: {
    enabled: 'No',
    sources: [],
  },
};

export const CONVERSATION_CONTROL_LIMITS = {
  user_wait_stop_signal: {
    minimum: 5,
    maximum: 45,
    optionStep: 1,
  },
  long_user_input_handling: {
    minimum: 0.5,
    maximum: 10,
    optionStep: 0.5,
  },
};

const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const normalizeYesNo = (value, fallback = 'No') => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['yes', 'true', '1', 'on'].includes(normalized)) return 'Yes';
  if (['no', 'false', '0', 'off'].includes(normalized)) return 'No';
  return fallback;
};

const normalizeNumber = (value, fallback, minimum, maximum) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < minimum || parsed > maximum) return fallback;
  return parsed;
};

const normalizeInstruction = (value, fallback) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value !== 'string') return fallback;
  return value.slice(0, CONVERSATION_CONTROL_INSTRUCTION_MAX_LENGTH);
};

export const normalizeConversationControls = (value) => {
  const source = isObject(value) ? value : {};
  const waitSource = isObject(source.user_wait_stop_signal) ? source.user_wait_stop_signal : {};
  const longInputSource = isObject(source.long_user_input_handling) ? source.long_user_input_handling : {};
  const externalContextSource = isObject(source.accept_external_context) ? source.accept_external_context : {};
  const preservedSource = { ...source };
  delete preservedSource.interruption_backoff;

  return {
    ...preservedSource,
    user_wait_stop_signal: {
      ...waitSource,
      enabled: normalizeYesNo(
        waitSource.enabled,
        CONVERSATION_CONTROL_DEFAULTS.user_wait_stop_signal.enabled,
      ),
      max_wait_seconds: normalizeNumber(
        waitSource.max_wait_seconds,
        CONVERSATION_CONTROL_DEFAULTS.user_wait_stop_signal.max_wait_seconds,
        CONVERSATION_CONTROL_LIMITS.user_wait_stop_signal.minimum,
        CONVERSATION_CONTROL_LIMITS.user_wait_stop_signal.maximum,
      ),
      instruction: normalizeInstruction(
        waitSource.instruction,
        CONVERSATION_CONTROL_DEFAULTS.user_wait_stop_signal.instruction,
      ),
    },
    long_user_input_handling: {
      ...longInputSource,
      enabled: normalizeYesNo(
        longInputSource.enabled,
        CONVERSATION_CONTROL_DEFAULTS.long_user_input_handling.enabled,
      ),
      continuation_timeout_seconds: normalizeNumber(
        longInputSource.continuation_timeout_seconds,
        CONVERSATION_CONTROL_DEFAULTS.long_user_input_handling.continuation_timeout_seconds,
        CONVERSATION_CONTROL_LIMITS.long_user_input_handling.minimum,
        CONVERSATION_CONTROL_LIMITS.long_user_input_handling.maximum,
      ),
      instruction: normalizeInstruction(
        longInputSource.instruction,
        CONVERSATION_CONTROL_DEFAULTS.long_user_input_handling.instruction,
      ),
    },
    accept_external_context: {
      ...externalContextSource,
      enabled: normalizeYesNo(
        externalContextSource.enabled,
        CONVERSATION_CONTROL_DEFAULTS.accept_external_context.enabled,
      ),
      sources: Array.isArray(externalContextSource.sources)
        ? [...new Set(externalContextSource.sources.filter((item) => item === 'whatsapp'))]
        : [],
    },
  };
};
